import {
  EVENT_AUTH_DISCARDED,
  EVENT_AUTH_EXPIRED,
  EVENT_AUTH_RENEW_ERROR,
  EVENT_AUTH_RENEW_SUCCESS,
} from "@sgwt/connect-core";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { debug } from "../utils";
import {
  clearLoginAttempts,
  hasTooManyAttemptedLogins,
  storeLastLoginAttempt,
} from "./infiniteAuthorizationLoopProtection";
import {
  ensureUserCanImpersonate,
  getNavigateAs,
  mapAudienceToUserType,
  setNavigateAs,
  setNavigateAsCookie,
} from "./navigateAs";
import { IUser, UserType, fetchUser } from "./services";
import { useSgConnect } from "./sgwt-connect";

export const useUser = ({ isSgInternal = false } = {}) => {
  const sgConnect = useSgConnect();

  const [user, setUser] = useState<IUser>();

  // called when user impersonates an audience
  // or after sgConnect.requestAuthorization OR sgConnect.renewAuthorization
  const refreshUser = useCallback(async () => {
    debug("refresh user");

    setNavigateAsCookie(undefined);
    const freshlyFetchedUser = await fetchUser();

    debug(`user audience: ${freshlyFetchedUser?.audience}`);

    ensureUserCanImpersonate({
      audience: freshlyFetchedUser.audience,
    });

    setNavigateAsCookie(mapAudienceToUserType(freshlyFetchedUser.audience));

    //  *remove* once provided by backend
    if (
      !freshlyFetchedUser.portalModuleBaseUrl &&
      (isSgInternal || freshlyFetchedUser.fromInternal)
    ) {
      freshlyFetchedUser.portalModuleBaseUrl =
        process.env.REACT_APP_SUPER_SECRET_PORTAL_URL;
    }

    const userHasChanged =
      JSON.stringify(freshlyFetchedUser) !== JSON.stringify(user);

    if (userHasChanged) {
      debug("user has changed");
      setUser(freshlyFetchedUser);
    }

    return freshlyFetchedUser;
  }, [isSgInternal, user]);

  // add events to call re
  useEffect(() => {
    // sgwtConnect is not yet initialized, wait for the magic to happen
    if (!sgConnect) {
      return;
    }

    const onSgconnectConnectionEvent = (e: string) => {
      if (e === EVENT_AUTH_RENEW_ERROR && user?.audience !== "pub") {
        sgConnect.requestAuthorization();
        return;
      }
      debug(`[SGConnect event] ${e}`);
      refreshUser();
    };

    const sgConnectConnectionEvents = [
      EVENT_AUTH_EXPIRED,
      EVENT_AUTH_RENEW_ERROR,
      EVENT_AUTH_RENEW_SUCCESS,
    ].map((x) => [x, () => onSgconnectConnectionEvent(x)] as const);

    sgConnectConnectionEvents.forEach(([event, listener]) => {
      sgConnect?.on(event, listener);
    });

    const error = sgConnect.getAuthorizationError();
    if (error) {
      // ERROR While the requesting authorization...
      document.body.innerHTML = `
          <div class="alert alert-danger" role="alert">
            Authorization error: ${error}
          </div>
        `;
    }

    // user is already logged on
    if (sgConnect.isAuthorized()) {
      clearLoginAttempts();
      debug("user is authorized");
      if (!user) refreshUser();
    } else if (isSgInternal || user?.fromInternal) {
      debug("requesting authorization");
      storeLastLoginAttempt();
      if (!hasTooManyAttemptedLogins()) {
        sgConnect?.requestAuthorization();
      } else {
        // the user has looped too many time between index.html and signin-oidc.html
        // don't try to login to avoid infinite loop
        // clear login attempts so that they can retry
        clearLoginAttempts();
      }
    } else {
      debug("renewing authorization");
      sgConnect?.renewAuthorization();
    }

    return () => {
      sgConnectConnectionEvents.forEach(([event, listener]) => {
        sgConnect?.removeListener(event, listener);
      });
    };
  }, [isSgInternal, refreshUser, sgConnect, user]);

  const setNavigateAsAndReload = useCallback(
    async (navigateAsRequested: UserType | undefined) => {
      const previousNavigateAs = getNavigateAs();
      debug(
        `navigate as clicked: requested: ${navigateAsRequested}, previous: ${previousNavigateAs}`
      );
      if (previousNavigateAs !== navigateAsRequested) {
        setNavigateAs(navigateAsRequested);
        refreshUser();
      }
    },
    [refreshUser]
  );

  return useMemo(
    () => ({
      user,
      setNavigateAs: setNavigateAsAndReload,
    }),
    [setNavigateAsAndReload, user]
  );
};

export type IUserContext = Pick<
  ReturnType<typeof useUser>,
  "user" | "setNavigateAs"
> & { isSgInternal: boolean };

const Context = createContext<IUserContext>({} as IUserContext);

export const useUserContext = () => {
  const ctx = useContext(Context);
  return ctx;
};

export const UserAuthorizationProvider = ({
  isSgInternal = false,
  children,
}: {
  children: React.ReactNode;
  isSgInternal: boolean;
}) => {
  const userContext = useUser({ isSgInternal });
  const ctx = useMemo(
    () => ({ isSgInternal, ...userContext }),
    [isSgInternal, userContext]
  );
  return <Context.Provider value={ctx}> {children}</Context.Provider>;
};

export const UserContextProvider = ({
  context,
  children,
}: {
  context: IUserContext;
  children: React.ReactNode;
}) => {
  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const useAuthorizedUserEmail = () => {
  const email = useUserContext().user?.email;
  if (!email) {
    throw Error("Expected authorized user");
  }
  return email;
};

export const withUserContext =
  <P extends object>(
    WrappedComponent: React.ComponentType<P & { context: IUserContext }>
  ): React.ComponentType<P> =>
  (props: P) => {
    const userContext = useUserContext();
    return <WrappedComponent {...props} context={userContext} />;
  };
